import React, { createContext, useState, useEffect, ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import * as authService from "../services/auth";
import { UserCredentials, AuthUser } from "../types/auth";
import { getDashboardRoute } from "../utils/roleUtils";
import OneSignal from "react-onesignal";

interface AuthContextType {
  user: AuthUser | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<boolean>;
  handleLogout: () => Promise<void>;
  navigateToDashboard: () => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Helper function to initialize OneSignal
  // This is called only after successful login to prevent premature subscription prompts
  const initializeOneSignal = async () => {
    try {
      // Check if already initialized
      const isInitialized = await OneSignal.context?.isInitialized();
      if (isInitialized) {
        console.log("[OneSignal] Already initialized");
        return true;
      }

      // Wait for service worker to be registered first
      if ("serviceWorker" in navigator) {
        const registration = await navigator.serviceWorker.ready;
        console.log(
          "[OneSignal] Service worker ready:",
          registration.active?.state,
        );
      }

      // Initialize OneSignal with configuration that works with our custom SW
      await OneSignal.init({
        appId: "c2149a7f-6c68-4edf-9ebe-7293c5aaeb36",
        allowLocalhostAsSecureOrigin: true,
        // Service worker is already registered by VitePWA
        serviceWorkerPath: "/sw.js",
        serviceWorkerParam: { scope: "/" },
        autoRegister: false,
        autoResubscribe: true,
        notifyButton: { enable: false },
      });

      console.log("[OneSignal] Initialized successfully");
      return true;
    } catch (error) {
      console.warn("[OneSignal] Initialization failed:", error);
      return false;
    }
  };

  // Helper function to identify user in OneSignal
  // IMPORTANT: Must be called AFTER OneSignal initialization
  // This ensures the external_id is set when the subscription is created
  const identifyUserInOneSignal = async (userId: string, userRole: string) => {
    const maxRetries = 5;
    const retryDelay = 1000; // 1 second

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        console.log(
          `[OneSignal] Attempt ${attempt}/${maxRetries} to identify user`,
        );

        // Check if OneSignal is actually initialized and ready
        const isInitialized = await OneSignal.context?.isInitialized();
        if (!isInitialized) {
          console.log("[OneSignal] Not initialized yet, waiting...");
          await new Promise((resolve) => setTimeout(resolve, retryDelay));
          continue;
        }

        // Call login() to set the external_id
        // This associates the push subscription with our user
        await OneSignal.login(userId);
        console.log("[OneSignal] User logged in:", userId);

        // Add role tag for segmentation
        await OneSignal.User.addTag("role", userRole);
        console.log(
          "[OneSignal] User identified successfully with role:",
          userRole,
        );

        return true;
      } catch (error) {
        console.warn(
          `[OneSignal] Identification attempt ${attempt} failed:`,
          error,
        );
        if (attempt < maxRetries) {
          await new Promise((resolve) => setTimeout(resolve, retryDelay));
        }
      }
    }
    console.error("[OneSignal] Failed to identify user after all retries");
    return false;
  };

  useEffect(() => {
    // Check if user is already logged in on app start
    const initAuth = async () => {
      try {
        const token = localStorage.getItem("token");
        if (token) {
          const response = await authService.getCurrentUser();
          if (response.success && response.user) {
            setUser(response.user);

            // Initialize and identify user in OneSignal (async, don't block page load)
            const userId = response.user.id || response.user._id;
            const userRole =
              response.user.role || response.user.roles?.[0] || "user";
            if (userId) {
              (async () => {
                try {
                  const initialized = await initializeOneSignal();
                  if (initialized) {
                    const identified = await identifyUserInOneSignal(
                      userId,
                      userRole,
                    );
                    // Don't show prompt on page load, only on fresh login
                    if (identified) {
                      console.log("[OneSignal] User identified on app start");
                    }
                  }
                } catch (error) {
                  console.warn(
                    "[OneSignal] App start identification failed:",
                    error,
                  );
                }
              })();
            }

            // If we're on the login page and user is already authenticated,
            // redirect to appropriate dashboard
            if (
              window.location.pathname === "/login" ||
              window.location.pathname === "/"
            ) {
              const dashboardRoute = getDashboardRoute(response.user);
              navigate(dashboardRoute, { replace: true });
            }
          }
        }
      } catch (error) {
        // Token might be invalid, remove it
        console.error("Auth initialization error:", error);
        localStorage.removeItem("token");
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, [navigate]);
  const handleLogin = async (
    email: string,
    password: string,
  ): Promise<boolean> => {
    try {
      setLoading(true);
      const credentials: UserCredentials = { email, password };
      const response = await authService.login(credentials);

      if (response.success && response.user) {
        setUser(response.user);
        localStorage.setItem("token", response.token);

        // Initialize OneSignal first, then identify user and prompt for notifications
        const userId = response.user.id || response.user._id;
        const userRole =
          response.user.role || response.user.roles?.[0] || "user";

        // Initialize and identify user in OneSignal (async, don't block login)
        if (userId) {
          (async () => {
            try {
              // Step 1: Initialize OneSignal
              const initialized = await initializeOneSignal();
              if (!initialized) {
                console.warn(
                  "[OneSignal] Initialization failed, skipping identification",
                );
                return;
              }

              // Step 2: Identify user in OneSignal
              const identified = await identifyUserInOneSignal(
                userId,
                userRole,
              );
              if (!identified) {
                console.warn(
                  "[OneSignal] User identification failed, skipping prompt",
                );
                return;
              }

              // Step 3: Only show prompt after successful identification
              const isPushEnabled = await OneSignal.Notifications.permission;
              console.log("[OneSignal] Push permission:", isPushEnabled);

              if (!isPushEnabled) {
                // Small delay to ensure everything is ready
                await new Promise((resolve) => setTimeout(resolve, 500));
                console.log("[OneSignal] Showing subscription prompt");
                await OneSignal.Slidedown.promptPush();
              }
            } catch (error) {
              console.warn("[OneSignal] Post-login setup failed:", error);
            }
          })();
        }

        // Navigate to dashboard immediately with the fresh user data
        const dashboardRoute = getDashboardRoute(response.user);
        navigate(dashboardRoute);

        return true;
      }
      return false;
    } catch (error: any) {
      console.error("Login error:", error);
      // Re-throw the error with the backend message
      const message =
        error.response?.data?.message ||
        "Login failed. Please check your credentials.";
      throw new Error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async (): Promise<void> => {
    try {
      // Logout from OneSignal
      try {
        const isInitialized = await OneSignal.context?.isInitialized();
        if (isInitialized) {
          await OneSignal.logout();
          console.log("[OneSignal] User logged out");
        }
      } catch (error) {
        console.warn("[OneSignal] Logout failed:", error);
      }

      await authService.logout();
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      setUser(null);
      localStorage.removeItem("token");
      navigate("/login");
    }
  };

  const navigateToDashboard = (userData?: AuthUser) => {
    const currentUser = userData || user;
    const dashboardRoute = getDashboardRoute(currentUser);
    navigate(dashboardRoute);
  };

  const refreshUser = async (): Promise<void> => {
    try {
      const token = localStorage.getItem("token");
      if (token) {
        const response = await authService.getCurrentUser();
        if (response.success && response.user) {
          setUser(response.user);
        }
      }
    } catch (error) {
      console.error("Error refreshing user data:", error);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login: handleLogin,
        handleLogout,
        navigateToDashboard,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export { AuthContext };
export type { AuthContextType };
